import React, { useCallback, useEffect, useState } from "react";
import ChainsTable from "../Partner/component/ChainsTable";
import { DebtBreadcrumb, DebtContainer, DebtTitle, BreadcrumbItem, BreadcrumbSeparator } from "../DebtManagement/styled/DebtManagementStyles";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import {
	LegalEntityDetail,
	LegalEntityKind,
	LegalEntityRow,
	MergeSuggestion,
	apiErrorText,
	getLegalEntity,
	getMergeSuggestions,
	listChains,
	listLegalEntities,
	mergeLegalEntities,
	removeChainLocation,
	updateLegalEntity,
} from "../../../services/LegalEntityService";

/**
 * LEGAL ENTITIES & CHAINS — Quân chốt 17/10/2026 (pháp nhân làm gốc).
 *
 * Ba khái niệm TÁCH BẠCH (backend: whalelo-backend/modules/legal-entity/README.md):
 *   · Legal entity (bên ký)  = AI ký hợp đồng, nhận tiền, đóng thuế. Mỗi station thuộc đúng 1 bên ký.
 *   · Mandate (uỷ quyền)     = QUAN HỆ giữa 2 bên ký: bên sở hữu cho bên quản lý xem / ký thay. Không tiền.
 *   · Chain (chuỗi)          = nhóm station hiện chung: tên chuỗi + bên quản lý.
 *
 * B1: màn này CHỈ đọc/ghi dữ liệu bên ký mới; chưa đổi gì ở rút tiền, ký văn bản, cổng trạm.
 * Giao diện TIẾNG ANH như cả Admin (luật Quân 25/08).
 */

type Tab = "merge" | "entities" | "chains";

const KIND_LABEL: Record<LegalEntityKind, string> = {
	company: "Company",
	household: "Household business",
	individual: "Individual",
	unknown: "Unknown",
};
const STATUS_TONE: Record<string, { bg: string; fg: string; label: string }> = {
	draft: { bg: "#fffaeb", fg: "#b54708", label: "Draft — check" },
	verified: { bg: "#ecfdf3", fg: "#027a48", label: "Verified" },
	archived: { bg: "#f2f4f7", fg: "#475467", label: "Merged" },
};

const cell: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid #eaecf0", verticalAlign: "top" };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 };
const btn = (primary = false): React.CSSProperties => ({
	padding: "6px 12px",
	borderRadius: 6,
	cursor: "pointer",
	border: primary ? 0 : "1px solid #d0d5dd",
	background: primary ? "#1570ef" : "#fff",
	color: primary ? "#fff" : "#344054",
});
const input: React.CSSProperties = { padding: "6px 10px", border: "1px solid #d0d5dd", borderRadius: 6, fontSize: 14, width: "100%", boxSizing: "border-box" };

const Pill: React.FC<{ status: string }> = ({ status }) => {
	const t = STATUS_TONE[status] || STATUS_TONE.draft;
	return <span style={{ background: t.bg, color: t.fg, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{t.label}</span>;
};

const LegalEntities: React.FC = () => {
	const [tab, setTab] = useState<Tab>("merge");
	const [notice, setNotice] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtContainer>
					<DebtBreadcrumb>
						<BreadcrumbItem as="a" href="/" style={{ textDecoration: "none" }}>
							{"Home"}
						</BreadcrumbItem>
						<BreadcrumbSeparator src="./static/img/direction-right.png" alt=">" />
						<BreadcrumbItem active>{"Legal entities & chains"}</BreadcrumbItem>
					</DebtBreadcrumb>
					<DebtTitle>Legal entities & chains</DebtTitle>

					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, margin: "8px 0 16px", maxWidth: 1100 }}>
						<Explain title="Legal entity" body="WHO signs the contract, receives payouts and pays tax — a company, a household business or an individual. Every station belongs to exactly one legal entity. Its registered address is the “head office”." />
						<Explain title="Mandate" body="A RELATIONSHIP between two legal entities: the owner of a station lets a managing entity view or sign on its behalf, from a start date. Never money when the entities differ." />
						<Explain title="Chain" body="A group of stations shown together, with its own name and a managing entity. One entity with many stations needs no mandate; many entities (e.g. Red Planet) need one mandate each." />
					</div>

					<div style={{ display: "flex", gap: 8, margin: "0 0 12px", flexWrap: "wrap" }}>
						{(
							[
								["merge", "Merge suggestions"],
								["entities", "Legal entities"],
								["chains", "Chains"],
							] as [Tab, string][]
						).map(([k, label]) => (
							<button
								key={k}
								type="button"
								onClick={() => {
									setTab(k);
									setNotice(null);
								}}
								style={{ ...btn(tab === k), borderRadius: 999, padding: "6px 14px" }}>
								{label}
							</button>
						))}
					</div>

					{notice ? <p style={{ color: notice.tone === "ok" ? "#027a48" : "#b42318", fontWeight: 600 }}>{notice.text}</p> : null}

					{tab === "merge" ? <MergeTab onNotice={setNotice} /> : null}
					{tab === "entities" ? <EntitiesTab onNotice={setNotice} /> : null}
					{tab === "chains" ? <ChainsTab onNotice={setNotice} /> : null}
				</DebtContainer>
			</OrderInner>
		</MyBookingWrapper>
	);
};

const Explain: React.FC<{ title: string; body: string }> = ({ title, body }) => (
	<div style={{ border: "1px solid #eaecf0", borderRadius: 10, padding: "10px 12px", background: "#fcfcfd" }}>
		<div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
		<div style={{ color: "#475467", fontSize: 13, lineHeight: 1.5 }}>{body}</div>
	</div>
);

type NoticeFn = (n: { tone: "ok" | "err"; text: string } | null) => void;

/* ── Merge suggestions: different legal entities whose stations use the SAME bank account ── */
const MergeTab: React.FC<{ onNotice: NoticeFn }> = ({ onNotice }) => {
	const [groups, setGroups] = useState<MergeSuggestion[]>([]);
	const [loading, setLoading] = useState(false);
	const [target, setTarget] = useState<Record<string, number>>({});
	const [busy, setBusy] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setGroups(await getMergeSuggestions());
		} catch (e) {
			onNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setLoading(false);
		}
	}, [onNotice]);
	useEffect(() => {
		load();
	}, [load]);

	const merge = async (g: MergeSuggestion) => {
		const to = target[g.soTaiKhoan] || g.legalEntityIds[0];
		const from = g.legalEntityIds.filter((x) => x !== to);
		if (!window.confirm(`Merge legal entities ${from.map((x) => "#" + x).join(", ")} into #${to}? Their stations will belong to #${to}. Payouts are not changed.`)) return;
		setBusy(g.soTaiKhoan);
		try {
			const r = await mergeLegalEntities(to, from);
			onNotice({ tone: "ok", text: `Merged into #${r.targetId}: ${r.diemChuyen} station(s) moved.` });
			await load();
		} catch (e) {
			onNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setBusy(null);
		}
	};

	return (
		<div>
			<p style={{ color: "#555", maxWidth: 900, lineHeight: 1.6 }}>
				Stations that declared the <b>same bank account</b> but sit under <b>different legal entities</b> — usually one owner with several logins.
				Merge only after checking the account holder and the business documents. The system never merges on its own.
			</p>
			<button type="button" onClick={load} disabled={loading} style={btn()}>
				{loading ? "Loading…" : "Refresh"}
			</button>
			{!loading && groups.length === 0 ? <p style={{ color: "#555" }}>No suggestions.</p> : null}
			<div style={{ overflowX: "auto", marginTop: 12 }}>
				{groups.length ? (
					<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
						<thead>
							<tr style={{ background: "#f9fafb", textAlign: "left" }}>
								<th style={cell}>Account holder</th>
								<th style={cell}>Account number</th>
								<th style={cell}>Stations</th>
								<th style={cell}>Keep legal entity</th>
								<th style={cell}></th>
							</tr>
						</thead>
						<tbody>
							{groups.map((g) => (
								<tr key={g.soTaiKhoan}>
									<td style={cell}>{g.chuTaiKhoan || "—"}</td>
									<td style={{ ...cell, ...mono }}>{g.soTaiKhoan}</td>
									<td style={cell}>
										{g.partnerIds.map((p) => (
											<a key={p} href={`/partner?filterKeyword=${p}`} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>
												#{p}
											</a>
										))}
									</td>
									<td style={cell}>
										<select
											value={target[g.soTaiKhoan] || g.legalEntityIds[0]}
											onChange={(e) => setTarget({ ...target, [g.soTaiKhoan]: Number(e.target.value) })}
											style={{ ...input, width: "auto" }}>
											{g.legalEntityIds.map((id) => (
												<option key={id} value={id}>
													#{id}
												</option>
											))}
										</select>
									</td>
									<td style={cell}>
										<button type="button" disabled={busy === g.soTaiKhoan} onClick={() => merge(g)} style={btn(true)}>
											{busy === g.soTaiKhoan ? "Merging…" : "Merge"}
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				) : null}
			</div>
		</div>
	);
};

/* ── Legal entities: list + detail with editable fields, stations and mandates ── */
const EntitiesTab: React.FC<{ onNotice: NoticeFn }> = ({ onNotice }) => {
	const [rows, setRows] = useState<LegalEntityRow[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState("");
	const [loading, setLoading] = useState(false);
	const [openId, setOpenId] = useState<number | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const r = await listLegalEntities({ page, perPage: 50, search: search.trim() || undefined, status: status || undefined });
			setRows(r.docs);
			setTotal(r.total);
		} catch (e) {
			onNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setLoading(false);
		}
	}, [page, search, status, onNotice]);
	useEffect(() => {
		load();
	}, [load]);

	return (
		<div>
			<div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
				<input
					placeholder="Search legal name, tax ID, registration no., email"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							setPage(1);
							load();
						}
					}}
					style={{ ...input, maxWidth: 380 }}
				/>
				<select value={status} onChange={(e) => (setStatus(e.target.value), setPage(1))} style={{ ...input, width: "auto" }}>
					<option value="">Active (draft + verified)</option>
					<option value="draft">Draft — check</option>
					<option value="verified">Verified</option>
					<option value="archived">Merged</option>
				</select>
				<span style={{ color: "#667085", fontSize: 13 }}>
					{loading ? "Loading…" : `${total} legal entit${total === 1 ? "y" : "ies"}`}
				</span>
			</div>
			<div style={{ overflowX: "auto", marginTop: 12 }}>
				<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
					<thead>
						<tr style={{ background: "#f9fafb", textAlign: "left" }}>
							<th style={cell}>ID</th>
							<th style={cell}>Legal name</th>
							<th style={cell}>Type</th>
							<th style={cell}>Country</th>
							<th style={cell}>Tax ID</th>
							<th style={cell}>Stations</th>
							<th style={cell}>Status</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((r) => (
							<React.Fragment key={r.id}>
								<tr onClick={() => setOpenId(openId === r.id ? null : r.id)} style={{ cursor: "pointer", background: openId === r.id ? "#f5f8ff" : undefined }}>
									<td style={{ ...cell, ...mono }}>#{r.id}</td>
									<td style={cell}>
										<b>{r.legalName}</b>
									</td>
									<td style={cell}>{KIND_LABEL[r.kind] || r.kind}</td>
									<td style={cell}>{r.countryCode || "—"}</td>
									<td style={{ ...cell, ...mono }}>{r.taxId || "—"}</td>
									<td style={{ ...cell, fontVariantNumeric: "tabular-nums" }}>
										{r.soDiem || 0}
										{r.dangBan ? <span style={{ color: "#667085" }}> · {r.dangBan} published</span> : null}
									</td>
									<td style={cell}>
										<Pill status={r.status} />
									</td>
								</tr>
								{openId === r.id ? (
									<tr>
										<td colSpan={7} style={{ ...cell, background: "#f9fafb" }}>
											<EntityDetail id={r.id} onSaved={load} onNotice={onNotice} />
										</td>
									</tr>
								) : null}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
			<div style={{ display: "flex", gap: 8, marginTop: 12 }}>
				<button type="button" style={btn()} disabled={page <= 1} onClick={() => setPage(page - 1)}>
					Previous
				</button>
				<span style={{ alignSelf: "center", color: "#667085" }}>Page {page}</span>
				<button type="button" style={btn()} disabled={page * 50 >= total} onClick={() => setPage(page + 1)}>
					Next
				</button>
			</div>
		</div>
	);
};

const EntityDetail: React.FC<{ id: number; onSaved: () => void; onNotice: NoticeFn }> = ({ id, onSaved, onNotice }) => {
	const [d, setD] = useState<LegalEntityDetail | null>(null);
	const [form, setForm] = useState<Partial<LegalEntityRow>>({});
	const [saving, setSaving] = useState(false);
	/* 17/10/2026 — Station là tên gốc: gọi chuỗi bằng TÊN Admin đặt, kèm số #storeId; không gọi "store". */
	const [tenChuoi, setTenChuoi] = useState<Record<number, string>>({});
	useEffect(() => {
		listChains()
			.then((rows) => setTenChuoi(Object.fromEntries(rows.filter((c) => c.name).map((c) => [c.id, String(c.name)]))))
			.catch(() => setTenChuoi({}));
	}, []);
	const nhanChuoi = (storeId: number | null | undefined) =>
		tenChuoi[Number(storeId)] ? `${tenChuoi[Number(storeId)]} (#${storeId})` : `#${storeId}`;

	useEffect(() => {
		getLegalEntity(id)
			.then((x) => {
				setD(x);
				setForm(x.legalEntity);
			})
			.catch((e) => onNotice({ tone: "err", text: apiErrorText(e) }));
	}, [id, onNotice]);

	if (!d) return <span style={{ color: "#667085" }}>Loading…</span>;
	const archived = d.legalEntity.status === "archived";

	const save = async (verify: boolean) => {
		setSaving(true);
		try {
			const patch: Partial<LegalEntityRow> = {
				legalName: (form.legalName || "").trim(),
				kind: form.kind,
				registrationNo: form.registrationNo || null,
				taxId: form.taxId || null,
				registeredAddress: form.registeredAddress || null,
				countryCode: form.countryCode ? form.countryCode.toUpperCase() : null,
				representativeName: form.representativeName || null,
				representativeEmail: form.representativeEmail || null,
				note: form.note || null,
				...(verify ? { status: "verified" as const } : {}),
			};
			await updateLegalEntity(id, patch);
			onNotice({ tone: "ok", text: verify ? `Legal entity #${id} saved and verified.` : `Legal entity #${id} saved.` });
			onSaved();
		} catch (e) {
			onNotice({ tone: "err", text: apiErrorText(e) });
		} finally {
			setSaving(false);
		}
	};

	const f = (key: keyof LegalEntityRow, label: string, placeholder = "") => (
		<label style={{ display: "block", fontSize: 13, color: "#475467" }}>
			{label}
			<input
				disabled={archived}
				value={(form[key] as string) || ""}
				placeholder={placeholder}
				onChange={(e) => setForm({ ...form, [key]: e.target.value })}
				style={{ ...input, marginTop: 4 }}
			/>
		</label>
	);

	return (
		<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
			<div style={{ display: "grid", gap: 8 }}>
				<b>Legal entity details</b>
				{archived ? <span style={{ color: "#b54708" }}>Merged into #{d.legalEntity.mergedIntoId} — read only.</span> : null}
				{f("legalName", "Registered legal name")}
				<label style={{ display: "block", fontSize: 13, color: "#475467" }}>
					Type
					<select
						disabled={archived}
						value={form.kind || "unknown"}
						onChange={(e) => setForm({ ...form, kind: e.target.value as LegalEntityKind })}
						style={{ ...input, marginTop: 4 }}>
						{Object.entries(KIND_LABEL).map(([k, v]) => (
							<option key={k} value={k}>
								{v}
							</option>
						))}
					</select>
				</label>
				{f("registrationNo", "Registration no.")}
				{f("taxId", "Tax ID")}
				{f("registeredAddress", "Registered address (head office)")}
				{f("countryCode", "Country (2 letters)", "VN")}
				{f("representativeName", "Representative")}
				{f("representativeEmail", "Representative email")}
				{f("note", "Internal note")}
				{!archived ? (
					<div style={{ display: "flex", gap: 8 }}>
						<button type="button" disabled={saving} onClick={() => save(false)} style={btn()}>
							Save
						</button>
						<button type="button" disabled={saving} onClick={() => save(true)} style={btn(true)}>
							Save & mark verified
						</button>
					</div>
				) : null}
			</div>
			<div>
				<b>Stations owned ({d.points.length})</b>
				<ul style={{ paddingLeft: 18, margin: "8px 0" }}>
					{d.points.map((p) => (
						<li key={p.id} style={{ fontSize: 13, padding: "2px 0" }}>
							<a href={`/partner?filterKeyword=${p.id}`} target="_blank" rel="noreferrer">
								#{p.id} {p.name}
							</a>{" "}
							<span style={{ color: "#667085" }}>
								· {p.lifecycleStatus}
								{p.storeId ? ` · chain ${nhanChuoi(p.storeId)}` : ""}
							</span>
							{p.storeId && Number(p.partnerRoleId) !== 2 ? (
								<button
									type="button"
									style={{ ...btn(), padding: "1px 8px", marginLeft: 8, fontSize: 12 }}
									onClick={async () => {
										if (!window.confirm(`Remove station #${p.id} ${p.name} from chain ${nhanChuoi(p.storeId)}? It becomes a single station: the chain account stops seeing its bookings and payouts. Nothing is deleted.`)) return;
										try {
											const r = await removeChainLocation(Number(p.storeId), p.id);
											onNotice({ tone: "ok", text: `Station #${r.partnerId} removed from chain ${nhanChuoi(r.storeId)}.` });
											onSaved();
											const x = await getLegalEntity(id);
											setD(x);
										} catch (e) {
											onNotice({ tone: "err", text: apiErrorText(e) });
										}
									}}>
									Remove from chain
								</button>
							) : null}
						</li>
					))}
				</ul>
				<b>Mandates</b>
				{d.mandatesGiven.length + d.mandatesReceived.length === 0 ? <p style={{ color: "#667085", fontSize: 13 }}>None.</p> : null}
				<ul style={{ paddingLeft: 18, margin: "8px 0", fontSize: 13 }}>
					{d.mandatesGiven.map((m) => (
						<li key={`g${m.id}`}>
							Lets #{m.managingEntityId} {scope(m)} since {day(m.validFrom)}
							{m.validTo ? ` until ${day(m.validTo)}` : ""} {m.authorityRef ? `· ref ${m.authorityRef}` : "· no signed authority yet"}
						</li>
					))}
					{d.mandatesReceived.map((m) => (
						<li key={`r${m.id}`}>
							Manages #{m.owningEntityId}: {scope(m)} since {day(m.validFrom)}
							{m.validTo ? ` until ${day(m.validTo)}` : ""} {m.authorityRef ? `· ref ${m.authorityRef}` : "· no signed authority yet"}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

const scope = (m: { scopeView: boolean; scopeSign: boolean }) => [m.scopeView ? "view" : null, m.scopeSign ? "sign" : null].filter(Boolean).join(" + ") || "nothing";
const day = (s: string) => (s ? new Date(s).toLocaleDateString("en-GB") : "—");

/* ── Chains: one row per chain, with an editable name (suggested from station names) ── */
/* 18/10/2026: bảng chuỗi dùng CHUNG với Station Management (chế độ xem Chains) — một bản code. */
const ChainsTab: React.FC<{ onNotice: NoticeFn }> = ({ onNotice }) => <ChainsTable onNotice={onNotice} />;

export default LegalEntities;
